import { useEffect, useRef } from "react";

/**
 * useCardSpotlight \u2014 attaches a delegated mousemove listener to a
 * parent container and updates two CSS custom properties on each
 * matched child element so a radial-gradient can track the cursor
 * inside that child.
 *
 * Why delegated (single listener on parent vs. one per card)?
 *   1. One handler instead of N \u2014 cheaper.
 *   2. Works even if cards are re-rendered/added dynamically.
 *   3. requestAnimationFrame coalesces updates to one per paint.
 *
 * The CSS that actually paints the glow lives in the consuming
 * component's stylesheet \u2014 it reads the --card-mx / --card-my
 * variables and renders a radial-gradient pseudo-element.
 */
export default function useCardSpotlight(parentRef, cardSelector) {
  const rafRef = useRef(null);

  useEffect(() => {
    const parent = parentRef.current;
    if (!parent) return;

    const onMove = (e) => {
      const card = e.target.closest(cardSelector);
      if (!card || !parent.contains(card)) return;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty("--card-mx", `${x}px`);
        card.style.setProperty("--card-my", `${y}px`);
      });
    };

    parent.addEventListener("mousemove", onMove);
    return () => {
      parent.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [parentRef, cardSelector]);
}
